import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { TradingViewChart } from '@/components/ui/trading-view-chart';
import { apiService } from '@/apiService';
import { BarData, ChartIndicatorData, IndicatorValue } from '@/types';
import signalRService, { BarState } from '@/services/signalRService';
import {
  Settings,
  TrendingUp,
  Eye,
  EyeOff,
  X
} from 'lucide-react';

interface AppliedIndicator {
  name: string;
  type: 'sma' | 'ema' | 'rsi' | 'macd' | 'bollinger';
  parameters: { [key: string]: any };
  color: string;
}

interface ModernChartProps {
  symbol?: string;
  timeframe?: string;
  isPopout?: boolean;
  onClose?: () => void;
  className?: string;
}

// Convert BarState to BarData
const convertBarStateToBarData = (barState: BarState): BarData => {
  return {
    symbol: barState.symbol || '',
    openBidPrice: barState.openBidPrice || 0,
    openAskPrice: barState.openAskPrice || 0,
    closeBidPrice: barState.closeBidPrice || 0,
    closeAskPrice: barState.closeAskPrice || 0,
    highBidPrice: barState.highBidPrice || 0,
    highAskPrice: barState.highAskPrice || 0,
    lowBidPrice: barState.lowBidPrice || 0,
    lowAskPrice: barState.lowAskPrice || 0,
    volume: barState.volume || 0,
    vwap: barState.vwap,
    tradeCount: barState.tradeCount,
    barStartTime: barState.barStartTime || '',
    barEndTime: barState.barEndTime || '',
    opened: barState.opened || false,
    closed: barState.closed || false,
    isFinal: barState.closed || false,
    open: barState.open,
    high: barState.high,
    low: barState.low,
    close: barState.close,
  };
};

// Handle bar updates with proper state management
const handleBarUpdate = (bars: BarData[], newBar: BarState): BarData[] => {
  const convertedBar: BarData = convertBarStateToBarData(newBar);
  const barTimestamp = new Date(newBar.barStartTime).getTime();

  if (isNaN(barTimestamp) || barTimestamp <= 0) {
    console.warn(`Invalid bar timestamp received: ${newBar.barStartTime}`);
    return bars;
  }

  const existingIndex = bars.findIndex(b =>
    new Date(b.barStartTime).getTime() === barTimestamp
  );

  if (existingIndex >= 0) {
    const existing = bars[existingIndex];

    if (existing.isFinal && !convertedBar.isFinal) {
      console.debug(`Ignoring in-progress update for finalized bar at ${newBar.barStartTime}`);
      return bars;
    }

    const updatedBars = [...bars];
    updatedBars[existingIndex] = convertedBar;
    console.log(`[handleBarUpdate] Updated existing bar at index ${existingIndex}, isFinal: ${convertedBar.isFinal}, OHLC: O=${convertedBar.open} H=${convertedBar.high} L=${convertedBar.low} C=${convertedBar.close}`);
    return updatedBars.sort((a, b) =>
      new Date(a.barStartTime).getTime() - new Date(b.barStartTime).getTime()
    );
  } else {
    const updatedBars = [...bars, convertedBar];
    console.log(`[handleBarUpdate] Added new bar, total bars: ${updatedBars.length}`);

    if (updatedBars.length > 200) {
      updatedBars.shift();
    }

    return updatedBars.sort((a, b) =>
      new Date(a.barStartTime).getTime() - new Date(b.barStartTime).getTime()
    );
  }
};

export const ModernChart: React.FC<ModernChartProps> = ({
  symbol: propSymbol = 'AAPL',
  timeframe: propTimeframe = '1M',
  isPopout = false,
  onClose,
  className
}) => {
  const componentId = React.useRef<string>();
  if (!componentId.current) {
    componentId.current = `${propSymbol}_${propTimeframe}_${Math.random().toString(36).substr(2, 9)}`;
    console.log(`[ModernChart] Component ${componentId.current} mounted`);
  }
  const [symbol, setSymbol] = useState(propSymbol);
  const [timeframe, setTimeframe] = useState(propTimeframe);
  const [bars, setBars] = useState<BarData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [showVolume, setShowVolume] = useState(false);
  const [indicators, setIndicators] = useState<AppliedIndicator[]>([]);
  const [crosshairData, setCrosshairData] = useState<{
    time?: number;
    price?: number;
  }>({});
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [serverIndicators, setServerIndicators] = useState<Record<string, ChartIndicatorData>>({});

  const timeframes = [
    { value: '1M', label: '1m' },
    { value: '5M', label: '5m' },
    { value: '15M', label: '15m' },
    { value: '30M', label: '30m' },
    { value: '1H', label: '1H' },
    { value: '4H', label: '4H' },
    { value: '1D', label: '1D' },
    { value: '1W', label: '1W' },
  ];

  const availableIndicators = [
    { type: 'sma', name: 'SMA', defaultParams: { period: 20 } },
    { type: 'ema', name: 'EMA', defaultParams: { period: 20 } },
    { type: 'rsi', name: 'RSI', defaultParams: { period: 14 } },
    { type: 'macd', name: 'MACD', defaultParams: { fast: 12, slow: 26, signal: 9 } },
    { type: 'bollinger', name: 'Bollinger Bands', defaultParams: { period: 20, stdDev: 2 } },
  ];

  // Initialize chart data
  const initializeChart = useCallback(async () => {
    try {
      setError(null);
      console.log(`[ModernChart EVENT] Fetching initial chart data`, {
        triggeredBy: 'initializeChart callback',
        symbol, timeframe, timestamp: new Date().toISOString(),
        componentId: componentId.current
      });
      const response = await apiService.getChartData(symbol, timeframe, 100);

      if (response.success && response.data && response.data.bars) {
        console.log(`[ModernChart EVENT] Initial data received`, {
          barCount: response.data.bars.length, symbol, timeframe
        });
        setBars(response.data.bars);

        // Load server-calculated indicators if present
        if (response.data.indicators) {
          setServerIndicators(response.data.indicators);
        }
      } else {
        if (response.error) {
          console.warn('No chart data received:', response.error);
        }
        setBars([]);
      }
    } catch (error) {
      console.error('Error loading chart data:', error);
      setError(`Failed to load chart data: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setBars([]);
    }
  }, [symbol, timeframe]);

  // Setup SignalR connection
  useEffect(() => {
    const setupSignalR = async () => {
      try {
        await signalRService.ensureStarted();
        setConnectionStatus('Connected');

        await signalRService.subscribeToSymbol(symbol);

        const chartId = `${symbol}_${timeframe}`;
        await signalRService.subscribeToChart(chartId);

        const listenerId = componentId.current!;

        signalRService.addBarUpdateListener(listenerId, (receivedSymbol: string, barState: BarState) => {
          if (receivedSymbol === symbol) {
            console.log(`[ModernChart EVENT] Bar update received for ${receivedSymbol}`, {
              time: barState.barStartTime, isFinal: barState.isFinal
            });

            setBars(prevBars => {
              const updated = handleBarUpdate(prevBars, barState);
              return updated;
            });
          }
        });

        signalRService.setCallbacks({
          onChartUpdated: (receivedChartId: string, chart: any) => {
            if (receivedChartId === chartId && chart.bars) {
              setBars(chart.bars);
            }
          }
        });

        // Listen for real-time indicator updates from backend
        signalRService.addIndicatorUpdateListener(listenerId, (receivedChartId: string, indicatorName: string, values: IndicatorValue[]) => {
          if (receivedChartId === chartId) {
            setServerIndicators(prev => ({
              ...prev,
              [indicatorName]: {
                name: indicatorName,
                values,
                lastUpdate: new Date().toISOString(),
              }
            }));
          }
        });

      } catch (error) {
        console.error('SignalR connection failed:', error);
        setConnectionStatus('Connection Failed');
      }
    };

    setupSignalR();

    return () => {
      const chartId = `${symbol}_${timeframe}`;
      signalRService.unsubscribeFromChart(chartId).catch(console.error);
      signalRService.unsubscribeFromSymbol(symbol).catch(console.error);
      if (componentId.current) {
        signalRService.removeBarUpdateListener(componentId.current);
        signalRService.removeIndicatorUpdateListener(componentId.current);
      }
    };
  }, [symbol, timeframe]);

  // Load initial chart data
  useEffect(() => {
    initializeChart();
  }, [initializeChart]);

  // Subscribe to symbol updates with specific timeframe
  useEffect(() => {
    const subscribeToSymbol = async () => {
      try {
        await apiService.subscribeToSymbol(symbol, timeframe);
      } catch (error: any) {
        if (!error.message?.includes('404')) {
          console.error(`Failed to subscribe to ${symbol} for ${timeframe}:`, error);
        }
      }
    };
    subscribeToSymbol();
  }, [symbol, timeframe]);

  const handleCrosshairMove = (time: any, price: number | undefined) => {
    setCrosshairData({ time: time ? time * 1000 : undefined, price });
  };

  const addIndicator = (type: AppliedIndicator['type'], parameters: any) => {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b'];
    const color = colors[indicators.length % colors.length];
    setIndicators(prev => [...prev, { name: type.toUpperCase(), type, parameters, color }]);
  };

  const handleTimeframeChange = (newTimeframe: string) => {
    setTimeframe(newTimeframe);
  };

  const removeIndicator = (index: number) => {
    setIndicators(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Card className={`border ${className}`}>
      <CardHeader className="py-2 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Symbol + Timeframe */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{symbol}</span>
              <span className="text-xs text-muted-foreground">/</span>
            </div>

            {/* Timeframe Buttons */}
            <div className="flex items-center gap-0.5">
              {timeframes.map((tf) => (
                <button
                  key={tf.value}
                  onClick={() => handleTimeframeChange(tf.value)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    timeframe === tf.value
                      ? 'bg-primary/15 text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  {tf.label}
                </button>
              ))}
            </div>

            {/* Connection Status */}
            <div className="flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${
                connectionStatus === 'Connected' ? 'bg-emerald-500' : 'bg-red-500'
              }`} />
              <span className="text-[11px] text-muted-foreground">{connectionStatus}</span>
            </div>

            {/* Crosshair Data */}
            {crosshairData.time && crosshairData.price && (
              <div className="text-[11px] text-muted-foreground font-mono tabular-nums">
                {new Date(crosshairData.time).toLocaleString()} - ${crosshairData.price.toFixed(2)}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            {/* Bar count */}
            {bars.length > 0 && (
              <span className="text-[10px] text-muted-foreground mr-2 tabular-nums">{bars.length} bars</span>
            )}

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowVolume(!showVolume)}
              className="h-7 w-7"
              title={showVolume ? 'Hide volume' : 'Show volume'}
            >
              {showVolume ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Add indicator">
                  <TrendingUp className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {availableIndicators.map((indicator) => (
                  <DropdownMenuItem
                    key={indicator.type}
                    onClick={() => addIndicator(indicator.type as AppliedIndicator['type'], indicator.defaultParams)}
                  >
                    {indicator.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Chart settings">
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Chart Settings</DialogTitle>
                  <DialogDescription>
                    Configure chart display options and select the symbol to track.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <label htmlFor="symbol" className="text-right text-sm font-medium">
                      Symbol
                    </label>
                    <div className="col-span-3">
                      <Select value={symbol} onValueChange={setSymbol}>
                        <SelectTrigger id="symbol">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AAPL">Apple Inc. (AAPL)</SelectItem>
                          <SelectItem value="MSFT">Microsoft Corp. (MSFT)</SelectItem>
                          <SelectItem value="GOOGL">Alphabet Inc. (GOOGL)</SelectItem>
                          <SelectItem value="TSLA">Tesla Inc. (TSLA)</SelectItem>
                          <SelectItem value="AMZN">Amazon.com Inc. (AMZN)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <label htmlFor="timeframe-select" className="text-right text-sm font-medium">
                      Time Frame
                    </label>
                    <div className="col-span-3">
                      <Select value={timeframe} onValueChange={handleTimeframeChange}>
                        <SelectTrigger id="timeframe-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeframes.map((tf) => (
                            <SelectItem key={tf.value} value={tf.value}>
                              {tf.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <label htmlFor="volume" className="text-right text-sm font-medium">
                      Volume
                    </label>
                    <div className="col-span-3">
                      <Button
                        id="volume"
                        variant={showVolume ? "default" : "outline"}
                        size="sm"
                        onClick={() => setShowVolume(!showVolume)}
                        className="w-full"
                      >
                        {showVolume ? 'Volume Enabled' : 'Volume Disabled'}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {isPopout && onClose && (
              <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Applied Indicators */}
        {indicators.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {indicators.map((indicator, index) => (
              <div
                key={index}
                className="flex items-center gap-1 px-2 py-0.5 bg-secondary/50 rounded text-[11px]"
              >
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: indicator.color }}
                />
                <span>{indicator.name}({Object.values(indicator.parameters).join(', ')})</span>
                <button
                  onClick={() => removeIndicator(index)}
                  className="ml-0.5 hover:text-destructive text-muted-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className="p-0">
        {error ? (
          <div className="flex items-center justify-center h-64 text-destructive text-sm">
            {error}
          </div>
        ) : bars.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground text-sm gap-2">
            <div className="h-6 w-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            <span>Loading {symbol} {timeframe}...</span>
          </div>
        ) : (
          <TradingViewChart
            data={bars}
            symbol={symbol}
            height={400}
            showVolume={showVolume}
            indicators={indicators}
            serverIndicators={serverIndicators}
            onCrosshairMove={handleCrosshairMove}
            className="px-2 pb-2"
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ModernChart;
